import React from 'react';
import { motion } from 'framer-motion';
import { styled } from '@linaria/react';

import { BREAKPOINTS } from '@/constants';

import { DemoInlineToggleControl } from '@/components/Demo';

import AxisArrow from './AxisArrow';

const Output = React.forwardRef(function Output(
  { state = {}, height = 300, wrapperStyle = {}, children },
  ref
) {
  const { showAxisLabels, ...vars } = state;

  const direction = vars['--direction'] || 'row';

  const showHorizontalAxis = !!(
    showAxisLabels && direction.match(/^row/)
  );
  const showVerticalAxis = !!(
    showAxisLabels && direction.match(/^column/)
  );

  return (
    <>
      {typeof showAxisLabels === 'boolean' && (
        <Header>
          <DemoInlineToggleControl
            key="showAxisLabels"
            id="showAxisLabels"
            label="Show Primary Axis"
            handleFocusColor={null}
            handleColor={
              showAxisLabels
                ? 'var(--color-text)'
                : 'var(--color-gray-400)'
            }
          />
        </Header>
      )}
      <OuterWrapper>
        <Wrapper
          ref={ref}
          style={{ ...vars, ...wrapperStyle, height }}
        >
          {children}
        </Wrapper>

        {typeof showHorizontalAxis === 'boolean' && (
          <>
            <HorizontalAxis
              style={{ opacity: showHorizontalAxis ? 1 : 0 }}
            >
              <span>Primary Axis</span>
              <AxisArrow
                isVisible={showHorizontalAxis}
                direction="horizontal"
              />
            </HorizontalAxis>
            <VerticalAxis
              style={{ opacity: showVerticalAxis ? 1 : 0 }}
            >
              <span>Primary Axis</span>
              <AxisArrow
                isVisible={showVerticalAxis}
                direction="vertical"
              />
            </VerticalAxis>
          </>
        )}
      </OuterWrapper>
    </>
  );
});

const OuterWrapper = styled.div`
  position: relative;
`;

const Header = styled.header`
  display: flex;
  justify-content: flex-end;

  /*
    The <Demo>’s OuterWrapper has 32px of top padding on desktop, which feels like *way* too much space. We’ll cut into it with negative margin. On mobile, the 32px of top padding turns into 16px (via --viewport-padding), which actually looks fine. So we’ll */
  @media ${BREAKPOINTS.smAndLarger} {
    margin-top: -16px;
  }
`;

const Wrapper = styled(motion.div)`
  display: flex;
  flex-direction: var(--direction);
  flex-wrap: var(--wrap);
  justify-content: var(--justify);
  align-items: var(--align-items);
  align-content: var(--align-content);
  gap: var(--gap);
  overflow: hidden;
  max-width: 100%;
  border-radius: 4px;
  padding: 2px;
  outline: 1px solid var(--color-gray-300);
`;

const Axis = styled.div`
  position: absolute;
  color: var(--color-text);
  transition: opacity 250ms;
  white-space: nowrap;

  & > span {
    font-weight: var(--font-weight-medium);
    text-shadow:
      0px 1px 2px hsl(210deg 30% 8% / 0.4),
      0px 2px 4px hsl(210deg 30% 8% / 0.3),
      0px 4px 8px hsl(210deg 30% 8% / 0.2),
      0px 8px 16px hsl(210deg 30% 8% / 0.1);
  }
`;

const HorizontalAxis = styled(Axis)`
  top: calc(50% - 8px);
  left: 6px;
  right: 6px;
  height: 16px;

  & > span {
    position: absolute;
    top: 0px;
    right: 24px;
    transform: translateY(-100%);
  }
`;

const VerticalAxis = styled(Axis)`
  left: calc(50% - 8px);
  top: 6px;
  bottom: 6px;
  width: 16px;

  & > span {
    position: absolute;
    bottom: 26px;
    left: 14px;
    transform-origin: bottom left;
    transform: rotate(90deg) translateX(-100%);
  }
`;

export default Output;
